/* eslint-disable import/prefer-default-export */
import { useRouter } from 'next/router'
import { useCallback } from 'react'

import { UrlObject } from 'url'

import {
  PaginatedAssetsDocument,
  LaunchIdAndNameFragmentDoc,
  useCreateAssetMutation,
  PaginatedAssetGroupsDocument,
  CountAssetsDocument,
} from '~generated/hooks'
import { LaunchIdAndNameFragment } from '~generated/operations'
import { AssetTypes, CreateAssetInput } from '~generated/types'
import { launchObjTypename } from '~lib/apollo/typenames'
import { CREATE_EVENTS } from '~lib/constants'
import { parseQueryParam } from '~lib/routes'
import useAnalytics from '~lib/segment/useSegmentAnalytics'
import { OrganizationNavigation, LaunchNavigation } from '~shared/navigation'

type CreateAssetParams = {
  cancelRedirectAfterCreation?: boolean
}

export function useCreateAsset({ cancelRedirectAfterCreation }: CreateAssetParams = {}) {
  const [createAssetMutation, { client, ...options }] = useCreateAssetMutation()

  const router = useRouter()
  const launchId = parseQueryParam(router.query, 'launchId')
  const organizationSlug = parseQueryParam(router.query, 'organizationSlug')

  const { sendAnalyticsEvent } = useAnalytics()

  const createAsset = useCallback(
    ({ name = 'Add title', type = AssetTypes.COPY, ...input }: Partial<CreateAssetInput> = {}) => {
      createAssetMutation({
        notifyOnNetworkStatusChange: true,
        awaitRefetchQueries: true,
        refetchQueries: [PaginatedAssetsDocument, PaginatedAssetGroupsDocument, CountAssetsDocument],
        variables: {
          input: {
            name,
            type,
            ...input,
          },
        },

        onCompleted: ({ createAsset }) => {
          let eventData: Record<string, any> = null

          // TODO - move redirect logic outside of the hook
          if (launchId) {
            const launch = client.readFragment<LaunchIdAndNameFragment>({
              id: client.cache.identify({ id: launchId, __typename: launchObjTypename }),
              fragment: LaunchIdAndNameFragmentDoc,
              fragmentName: 'LaunchIdAndName',
            })

            eventData = {
              type: 'Inside launch',
              launchName: launch?.name,
            }
          } else if (createAsset.brandAsset) {
            eventData = {
              type: 'Brand asset',
            }
          }

          sendAnalyticsEvent(CREATE_EVENTS.CREATE_COPY_ASSET, eventData)

          if (cancelRedirectAfterCreation) return

          let url: UrlObject

          if (launchId) {
            url = {
              pathname: LaunchNavigation.launch.assets.asset.route.pathname,
              query: { organizationSlug, launchId, assetId: createAsset.id, firstVisit: true },
            }
          } else if (createAsset.brandAsset) {
            url = {
              pathname: OrganizationNavigation.organization.assets.brand.asset.route.pathname,
              query: { organizationSlug, assetId: createAsset.id, firstVisit: true },
            }
          } else {
            url = {
              pathname: OrganizationNavigation.organization.assets.asset.route.pathname,
              query: { organizationSlug, assetId: createAsset.id, firstVisit: true },
            }
          }

          router.push(url)
        },
      })
    },
    [createAssetMutation, sendAnalyticsEvent, cancelRedirectAfterCreation, launchId, router, client, organizationSlug]
  )

  return [createAsset, { client, ...options }] as const
}
